"use client";

import { ctaClick, tabSwitched } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

/**
 * Program Fee 1066 — KUK Fee Structure.
 *
 * Port of kuk-campus-portal `components/Degree/FeePlan.tsx`: a heading over a pill
 * switch for the student type and a row of plan cards, each a headline amount
 * (optionally split into a first-semester and a later-semester figure), an optional
 * list of additional fees, a "Popular" badge and a Get Started CTA — with a footnote
 * under the row whose figures change with the selected student type.
 *
 * DATA — the Program Fee 2 JSON store (`program_fee_v2`), unchanged, so nothing new is
 * kept and the section is routed through `ProgramFeeV2Service` in the controllers'
 * variant service map:
 *   `categories[]`          = the plan cards, grouped into tabs by `tab_group`
 *   `categories[].metadata` = that card's amount lines
 *   `metadata_1[]`          = the per-tab footnote, matched on `label`
 * A line marked `is_total` is a HEADLINE figure — the big number at the top of the
 * card — and one that is not is an additional-fee line with a tick beside it. v1's
 * "1st semester" / "2nd sem onwards" split is simply two headline lines, so a plan
 * that quotes one figure and a plan that quotes two need no separate shape.
 *
 * WHY NOT THE PROGRAM FEE 1 MATRIX that Program Fee 1043 reuses. That store models a
 * grid of amounts at (line item x plan), which is BDU's table. KUK's card carries at
 * most two headline figures and a short list of extras, and its "Popular" flag and
 * per-tab footnote have no cell to live in — expressing it as a matrix would mean new
 * columns on three tables to say what the JSON already says.
 *
 * ONE CARD COMPONENT, NOT TWO. v1 writes the entire card out twice — once under
 * `forIndianStudents` and once under `forInternationalStudents` — duplicating ~150
 * lines that differ only in which rows they filter. Here the tab picks the cards and
 * one card renders them, which is the same output and cannot drift between tabs.
 *
 * THE TAB SWITCH HIDES ITSELF when every card sits in one group, as it does in the
 * reference (v1 gates the `TabsList` on there being any international plan).
 *
 * AMOUNTS are printed as stored. v1 runs them through `formatCurrency(…, "INR")`
 * because it receives raw integers from its API; here the field is free text, so an
 * editor writes "₹2,18,990" — or "On request" — and gets exactly that.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:` is
 * 768px. This app remaps `md:` to 1230px and exposes 768px as `tab:` (see globals.css
 * `--breakpoint-*`), so every `md:` in the reference is ported as `tab:`.
 *
 * NOT CARRIED OVER — v1's `isUserVerified` branch, which swaps Get Started for a
 * "Continue Application" link to the admission portal, and its `getPath()` guard.
 * Both are per-visitor or per-route state, not page content.
 */

/** The 18x15 tick the reference draws beside each additional-fee line. */
const Tick = ({ color }: { color: string }) => (
  <svg
    width="18"
    height="15"
    viewBox="0 0 18 15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    className="shrink-0"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18.0011 3.3544C18.0011 3.64474 17.885 3.93508 17.676 4.14413L7.68823 14.1319C7.47918 14.3409 7.18884 14.457 6.8985 14.457C6.60816 14.457 6.31782 14.3409 6.10877 14.1319L0.325182 8.34826C0.116136 8.13922 0 7.84888 0 7.55853C0 7.26819 0.116136 6.97785 0.325182 6.76881L1.90464 5.18935C2.11368 4.98031 2.40402 4.86417 2.69436 4.86417C2.9847 4.86417 3.27505 4.98031 3.48409 5.18935L6.8985 8.61537L14.517 0.985216C14.7261 0.77617 15.0164 0.660034 15.3068 0.660034C15.5971 0.660034 15.8875 0.77617 16.0965 0.985216L17.676 2.56467C17.885 2.77372 18.0011 3.06406 18.0011 3.3544Z"
      fill={color}
    />
  </svg>
);


/**
 * v1's `.custom-underline` — a short accent rule under the section heading, used on
 * 12 headings across the reference. Defined in ITS globals.css as a `::after` with
 * `bottom:-6px; left:0; width:33%; height:5px; background:#9f1c33`.
 *
 * Reproduced as a real element rather than a pseudo-element so the colour can come
 * from a CMS field (a pseudo-element cannot read an inline style). `w-fit` on the
 * heading is load-bearing: the rule is 33% of the HEADING, and a full-width h2 would
 * stretch it across a third of the page.
 */
const HEADING_UNDERLINE = "absolute -bottom-1.5 left-0 h-[5px] w-1/3";

function isExplicitlyOff(v: number | boolean | string | undefined | null): boolean {
  return v === 0 || v === false || v === "0";
}

interface CategoryRow {
  id?: string;
  /** Caption under a headline figure, or the name of an additional-fee line. */
  label?: string;
  value?: string;
  /** True → a headline figure; false → an additional-fee line. */
  is_total?: boolean;
  status?: boolean;
  priority?: number;
  text_color?: string;
  value_text_color?: string;
  value_font_size?: string;
}

interface PlanCard {
  id?: string;
  /** Plan name, e.g. "Lumpsum" or "Semester". */
  title?: string;
  /** Student type this card belongs to; blank groups it with the first tab. */
  tab_group?: string;
  /** Card fill — set it to mark a card as the highlighted one. */
  bg_color?: string;
  text_color?: string;
  /** Badge in the card's top-right; blank hides it. */
  badge_text?: string;
  order_index?: number;
  metadata?: CategoryRow[];
}

interface FootnoteRow {
  id?: string;
  /** Must match a card's `tab_group`. */
  label?: string;
  /** Footnote copy (rich text). */
  description?: string;
  text_color?: string;
  priority?: number;
  status?: boolean;
}

interface ProgramFee1066Data {
  categories?: PlanCard[];
  /** Per-tab footnotes. */
  metadata_1?: FootnoteRow[];
  title_1?: string;
  title_1_color?: string;
  /** "Additional Fee:" label above the extras list. */
  metadata_title?: string;
  metadata_title_color?: string;
  background_color?: string;
  /** Default card surface, used by a card that sets no `bg_color`. */
  table_gradient_from?: string;
  /** Badge fill. */
  table_gradient_to?: string;
  /** Student-type switch track + card CTA fill. */
  cta_gradient_from?: string;
  /** Selected pill fill + the tick beside each additional-fee line. */
  title_2_gradient_from?: string;
  /** Caption colour under a headline figure. */
  title_2_gradient_to?: string;
  cta_text?: string;
  cta_text_color?: string;
  /** Accent rule under the heading (v1's `.custom-underline`). */
  gradient_color_1?: string;
  status?: boolean | number;
}

/** The payload arrives at one or two levels of `data` nesting. */
function getSection(data: any): ProgramFee1066Data {
  if (!data || typeof data !== "object") return {};
  const root = data as Record<string, unknown>;
  if (Array.isArray(root.categories)) return data as ProgramFee1066Data;
  const once = root.data;
  if (once && typeof once === "object") return getSection(once);
  return {};
}

const ProgramFee1066 = (props: { data?: any }) => {
  const sectionData = getSection(props?.data);
  const dispatch = useDispatch();

  const {
    title_1 = "Fee Structure",
    title_1_color = "#101112",
    metadata_title = "Additional Fee:",
    metadata_title_color = "#101112",
    background_color = "#FFFFFF",
    table_gradient_from = "#FFFFFF",
    table_gradient_to = "#FFCF88",
    cta_gradient_from = "#F3F3F3",
    title_2_gradient_from = "#9F1C33",
    title_2_gradient_to = "#9C9C9C",
    cta_text = "Get Started",
    cta_text_color = "#101112",
    gradient_color_1 = "#9F1C33",
  } = sectionData;

  const status = isCmsShowFlagOn(sectionData?.status, true);

  const cards = useMemo(
    () =>
      (Array.isArray(sectionData.categories) ? sectionData.categories : [])
        .slice()
        .sort((a, b) => (Number(a?.order_index) || 0) - (Number(b?.order_index) || 0)),
    [sectionData.categories],
  );

  /** Student-type tabs, in the order their first card appears. */
  const tabs = useMemo(() => {
    const seen: string[] = [];
    cards.forEach((c) => {
      const key = (c.tab_group || "").trim();
      if (!seen.includes(key)) seen.push(key);
    });
    return seen;
  }, [cards]);

  const [selected, setSelected] = useState("");

  useEffect(() => {
    if (tabs.length === 0) return;
    setSelected((prev) => (tabs.includes(prev) ? prev : tabs[0]));
  }, [tabs]);

  const activeTab = tabs.includes(selected) ? selected : tabs[0] || "";

  const visibleCards = useMemo(
    () => cards.filter((c) => (c.tab_group || "").trim() === activeTab),
    [cards, activeTab],
  );

  const footnote = useMemo(() => {
    const rows = sectionData.metadata_1;
    if (!Array.isArray(rows)) return undefined;
    return rows
      .filter((r) => !isExplicitlyOff(r?.status))
      .find((r) => (r.label || "").trim() === activeTab);
  }, [sectionData.metadata_1, activeTab]);

  const handleCtaClick = useCallback(
    (planLabel: string) => {
      ctaClick({
        section: "program_fee_1066",
        component: "fee_get_started",
        label: planLabel || cta_text || "get_started",
        step: "1",
        entity: "program_fee_1066",
        parentEntity: "program_fee_1066",
      });
      dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
    },
    [cta_text, dispatch],
  );

  if (!status || cards.length === 0) return null;

  const activeRows = (card: PlanCard) =>
    (Array.isArray(card.metadata) ? card.metadata : [])
      .filter((r) => !isExplicitlyOff(r?.status) && (r?.value || r?.label))
      .sort((a, b) => (Number(a?.priority) || 0) - (Number(b?.priority) || 0));

  return (
    <section
      className="w-full p-10 tab:p-20"
      style={{ backgroundColor: background_color }}
    >
      <div className="mx-auto w-full max-w-[1120px]">
        {title_1 ? (
          <h2
            className="relative w-fit text-2xl font-bold tab:text-4xl"
            style={{ color: title_1_color }}
          >
            {title_1}
            <span
              aria-hidden
              className={HEADING_UNDERLINE}
              style={{ backgroundColor: gradient_color_1 }}
            />
          </h2>
        ) : null}

        <div className="w-full py-10">
          {/* v1 hides the switch when only one student type has plans. */}
          {tabs.length > 1 ? (
            <div
              className="flex h-[38px] w-full items-center rounded-md p-1 tab:w-[40%]"
              style={{ backgroundColor: cta_gradient_from }}
            >
              {tabs.map((tab) => {
                const active = tab === activeTab;
                return (
                  <button
                    key={tab || "default"}
                    type="button"
                    onClick={() => {
                      setSelected(tab);
                      tabSwitched({
                        section: "fee_structure",
                        component: tab,
                        label: tab,
                      });
                    }}
                    aria-pressed={active}
                    className="h-full flex-1 rounded-md text-sm font-medium transition-colors"
                    style={{
                      backgroundColor: active ? title_2_gradient_from : "transparent",
                      color: active ? "#FFFFFF" : title_1_color,
                    }}
                  >
                    {tab}
                  </button>
                );
              })}
            </div>
          ) : null}

          <div
            className={cn(
              "mt-14 flex flex-col items-center justify-center gap-4",
              "tab:flex-row tab:items-stretch",
              visibleCards.length > 3 && "tab:justify-start tab:overflow-x-auto",
            )}
          >
            {visibleCards.map((card, index) => {
              const rows = activeRows(card);
              const headline = rows.filter((r) => r.is_total);
              const extras = rows.filter((r) => !r.is_total);
              const copyColor = card.text_color || title_1_color;
              return (
                <div
                  key={card.id ?? index}
                  className={cn(
                    "relative flex w-full flex-col justify-between gap-7 rounded-lg",
                    "px-6 pb-8 pt-12 shadow-xl",
                    "tab:min-h-[458px] tab:w-[326px] tab:min-w-[326px] tab:items-center",
                    headline.length > 1 && "tab:!min-w-[350px]",
                  )}
                  style={{ backgroundColor: card.bg_color || table_gradient_from }}
                >
                  {card.badge_text ? (
                    <span
                      className={cn(
                        "absolute right-0 top-0 flex items-center px-14 py-1",
                        "rounded-bl-3xl rounded-tr-lg text-sm font-semibold",
                      )}
                      style={{
                        backgroundColor: table_gradient_to,
                        color: title_1_color,
                      }}
                    >
                      {card.badge_text}
                    </span>
                  ) : null}

                  {card.title ? (
                    <p
                      className="w-full text-2xl font-medium leading-5 tab:text-center"
                      style={{ color: copyColor }}
                    >
                      {card.title}
                    </p>
                  ) : null}

                  {headline.length > 0 ? (
                    <div
                      className={cn(
                        "flex w-full flex-col text-[36px] font-extrabold",
                        headline.length > 1
                          ? "tab:flex-row tab:items-start tab:justify-start"
                          : "tab:items-center tab:justify-center tab:text-center",
                      )}
                      style={{ color: copyColor }}
                    >
                      {headline.map((row, i) => (
                        <span
                          key={row.id ?? i}
                          className={cn(
                            "flex flex-col",
                            headline.length > 1 &&
                              i === 0 &&
                              "items-start justify-start pr-[10px] tab:border-r",
                            headline.length > 1 && i > 0 && "pt-2 tab:pl-[10px] tab:pt-0",
                          )}
                        >
                          <span
                            style={{
                              color: row.value_text_color || undefined,
                              fontSize: row.value_font_size || undefined,
                            }}
                          >
                            {row.value}
                          </span>
                          {row.label ? (
                            <span
                              className="text-left text-base font-light"
                              style={{ color: row.text_color || title_2_gradient_to }}
                            >
                              {row.label}
                            </span>
                          ) : null}
                        </span>
                      ))}
                    </div>
                  ) : null}

                  {extras.length > 0 ? (
                    <div className="flex w-full flex-col items-start justify-start gap-2 tab:w-3/4 tab:pr-4">
                      {metadata_title ? (
                        <p className="text-xs" style={{ color: metadata_title_color }}>
                          {metadata_title}
                        </p>
                      ) : null}
                      {extras.map((row, i) => (
                        <span
                          key={row.id ?? i}
                          className="flex items-center gap-[10px] text-sm"
                          style={{ color: row.text_color || copyColor }}
                        >
                          <Tick color={title_2_gradient_from} />
                          {row.label}
                          {row.value ? (
                            <span
                              className="font-bold"
                              style={{ color: row.value_text_color || undefined }}
                            >
                              {row.value}
                            </span>
                          ) : null}
                        </span>
                      ))}
                    </div>
                  ) : null}

                  {cta_text ? (
                    <button
                      type="button"
                      onClick={() => handleCtaClick(card.title || "")}
                      className={cn(
                        "min-h-[50px] w-full rounded-md px-10 py-3 text-base font-medium",
                        "tab:w-[200px]",
                      )}
                      style={{
                        backgroundColor: cta_gradient_from,
                        color: cta_text_color,
                      }}
                    >
                      {cta_text}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>

        {footnote?.description ? (
          <div
            className={cn(
              "w-full text-left text-sm",
              "[&_a]:text-inherit [&_a]:underline",
            )}
            style={{ color: footnote.text_color || title_1_color }}
            dangerouslySetInnerHTML={{ __html: footnote.description }}
          />
        ) : null}
      </div>
    </section>
  );
};

export default ProgramFee1066;
